import { MessageList } from '@mastra/core/agent';
import type { MastraMessageContentV2 } from '@mastra/core/agent';
import { ErrorCategory, ErrorDomain, MastraError } from '@mastra/core/error';
import type { MastraDBMessage, StorageThreadType } from '@mastra/core/memory';
import {
  isRunFenceConflictError,
  MemoryStorage,
  resolveRunFence,
  TABLE_MEMORY_RUN_FENCES,
  TABLE_RESOURCES,
  TABLE_THREADS,
  TABLE_MESSAGES,
  normalizePerPage,
  calculatePagination,
  createStorageErrorId,
  ensureDate,
  filterByDateRange,
  storageMessageMatchesMetadataFilter,
  validateStorageMetadataFilter,
} from '@mastra/core/storage';
import type {
  RunFence,
  StorageResourceType,
  StorageListMessagesInput,
  StorageListMessagesOutput,
  StorageListThreadsInput,
  StorageListThreadsOutput,
  ThreadOrderBy,
  ThreadSortDirection,
  StorageCloneThreadInput,
  StorageCloneThreadOutput,
  ThreadCloneMetadata,
} from '@mastra/core/storage';
import type { Redis } from '@upstash/redis';
import { UpstashDB, resolveUpstashConfig } from '../../db';
import type { UpstashDomainConfig } from '../../db';
import { assertRunFence, raiseRunFence, retireRunFence, runClaimKey, writeBatch } from '../run-fencing';
import type { RunFenceCheck } from '../run-fencing';
import { getKey, processRecord } from '../utils';

function getThreadMessagesKey(threadId: string): string {
  return `thread:${threadId}:messages`;
}

function getMessageKey(threadId: string, messageId: string): string {
  const key = getKey(TABLE_MESSAGES, { threadId, id: messageId });
  return key;
}

// Index key for fast message ID -> threadId lookup (backwards compatible)
function getMessageIndexKey(messageId: string): string {
  return `msg-idx:${messageId}`;
}

export class StoreMemoryUpstash extends MemoryStorage {
  override readonly supportsPartialThreadUpdate = true;
  private client: Redis;
  #db: UpstashDB;
  constructor(config: UpstashDomainConfig) {
    super();
    const client = resolveUpstashConfig(config);
    this.client = client;
    this.#db = new UpstashDB({ client });
  }

  override supportsRunFencing(): boolean {
    return true;
  }

  override async raiseRunFence(fence: RunFence): Promise<boolean> {
    try {
      return await raiseRunFence(this.client, fence);
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'RAISE_RUN_FENCE', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { runId: fence.runId },
        },
        error,
      );
    }
  }

  override async retireRunFence(fence: RunFence): Promise<boolean> {
    try {
      return await retireRunFence(this.client, fence);
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'RETIRE_RUN_FENCE', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { runId: fence.runId },
        },
        error,
      );
    }
  }

  #runFenceCheck(fence: RunFence | undefined, operation: string): RunFenceCheck | undefined {
    const resolved = resolveRunFence(this, fence);
    return resolved && { claims: TABLE_MEMORY_RUN_FENCES, fence: resolved, operation };
  }

  async dangerouslyClearAll(): Promise<void> {
    await this.#db.deleteData({ tableName: TABLE_THREADS });
    await this.#db.deleteData({ tableName: TABLE_MESSAGES });
    await this.#db.deleteData({ tableName: TABLE_RESOURCES });
    await this.#db.scanAndDelete(`${runClaimKey(TABLE_MEMORY_RUN_FENCES, '')}*`);
  }

  async getThreadById({
    threadId,
    resourceId,
  }: {
    threadId: string;
    resourceId?: string;
  }): Promise<StorageThreadType | null> {
    try {
      const thread = await this.#db.get<StorageThreadType>({
        tableName: TABLE_THREADS,
        keys: { id: threadId },
      });

      if (!thread || (resourceId !== undefined && thread.resourceId !== resourceId)) return null;

      return {
        ...thread,
        createdAt: ensureDate(thread.createdAt)!,
        updatedAt: ensureDate(thread.updatedAt)!,
        metadata: typeof thread.metadata === 'string' ? JSON.parse(thread.metadata) : thread.metadata,
      };
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'GET_THREAD_BY_ID', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId,
          },
        },
        error,
      );
    }
  }

  public async listThreads(args: StorageListThreadsInput): Promise<StorageListThreadsOutput> {
    const { page = 0, perPage: perPageInput, orderBy, filter } = args;
    const { field, direction } = this.parseOrderBy(orderBy);

    try {
      // Validate pagination input before normalization
      // This ensures page === 0 when perPageInput === false
      this.validatePaginationInput(page, perPageInput ?? 100);
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'LIST_THREADS', 'INVALID_PAGE'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.USER,
          details: { page, ...(perPageInput !== undefined && { perPage: perPageInput }) },
        },
        error instanceof Error ? error : new Error('Invalid pagination parameters'),
      );
    }

    const perPage = normalizePerPage(perPageInput, 100);

    // Validate metadata keys to prevent prototype pollution and ensure safe key patterns
    try {
      this.validateMetadataKeys(filter?.metadata);
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'LIST_THREADS', 'INVALID_METADATA_KEY'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.USER,
          details: { metadataKeys: filter?.metadata ? Object.keys(filter.metadata).join(', ') : '' },
        },
        error instanceof Error ? error : new Error('Invalid metadata key'),
      );
    }

    const { offset, perPage: perPageForResponse } = calculatePagination(page, perPageInput, perPage);

    try {
      let allThreads: StorageThreadType[] = [];
      const pattern = `${TABLE_THREADS}:*`;
      const keys = await this.#db.scanKeys(pattern);

      // Return early if no keys found to avoid "Pipeline is empty" error
      if (keys.length === 0) {
        return {
          threads: [],
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      const pipeline = this.client.pipeline();
      keys.forEach(key => pipeline.get(key));
      const results = await pipeline.exec();

      for (let i = 0; i < results.length; i++) {
        const thread = results[i] as StorageThreadType | null;
        if (!thread) continue;

        // Apply resourceId filter if provided
        if (filter?.resourceId && thread.resourceId !== filter.resourceId) {
          continue;
        }

        // Apply metadata filters if provided (AND logic)
        if (filter?.metadata && Object.keys(filter.metadata).length > 0) {
          const threadMetadata = typeof thread.metadata === 'string' ? JSON.parse(thread.metadata) : thread.metadata;
          const matches = Object.entries(filter.metadata).every(([key, value]) => threadMetadata?.[key] === value);
          if (!matches) continue;
        }

        allThreads.push({
          ...thread,
          createdAt: ensureDate(thread.createdAt)!,
          updatedAt: ensureDate(thread.updatedAt)!,
          metadata: typeof thread.metadata === 'string' ? JSON.parse(thread.metadata) : thread.metadata,
        });
      }

      // Apply sorting with parameters
      const sortedThreads = this.sortThreads(allThreads, field, direction);

      const total = sortedThreads.length;
      // When perPage is false (get all), ignore page offset
      const end = perPageInput === false ? total : offset + perPage;
      const paginatedThreads = sortedThreads.slice(offset, end);
      const hasMore = perPageInput === false ? false : end < total;

      return {
        threads: paginatedThreads,
        total,
        page,
        perPage: perPageForResponse,
        hasMore,
      };
    } catch (error) {
      // Re-throw USER errors (validation errors) directly so callers get proper 400 responses
      if (error instanceof MastraError && error.category === ErrorCategory.USER) {
        throw error;
      }
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'LIST_THREADS', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            ...(filter?.resourceId && { resourceId: filter.resourceId }),
            hasMetadataFilter: !!filter?.metadata,
            page,
            perPage,
          },
        },
        error,
      );
      this.logger?.trackException(mastraError);
      this.logger.error(mastraError.toString());
      throw mastraError;
    }
  }

  async saveThread({ thread, fence }: { thread: StorageThreadType; fence?: RunFence }): Promise<StorageThreadType> {
    const check = this.#runFenceCheck(fence, 'saveThread');
    try {
      const { key, processedRecord } = processRecord(TABLE_THREADS, thread);
      const batch = writeBatch(this.client, check);
      batch.set(key, processedRecord);
      await batch.exec();
      return thread;
    } catch (error) {
      if (isRunFenceConflictError(error)) throw error;
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'SAVE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId: thread.id,
          },
        },
        error,
      );
      this.logger?.trackException(mastraError);
      this.logger.error(mastraError.toString());
      throw mastraError;
    }
  }

  async updateThread({
    id,
    title,
    metadata,
    fence,
  }: {
    id: string;
    title?: string;
    metadata?: Record<string, unknown>;
    fence?: RunFence;
  }): Promise<StorageThreadType> {
    const thread = await this.getThreadById({ threadId: id });
    if (!thread) {
      throw new MastraError({
        id: createStorageErrorId('UPSTASH', 'UPDATE_THREAD', 'FAILED'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.USER,
        text: `Thread ${id} not found`,
        details: {
          threadId: id,
        },
      });
    }

    const now = new Date();
    const updatedThread = {
      ...thread,
      title: title ?? thread.title,
      metadata: {
        ...thread.metadata,
        ...metadata,
      },
      updatedAt: now,
    };

    try {
      await this.saveThread({ thread: updatedThread, fence });
      return updatedThread;
    } catch (error) {
      if (isRunFenceConflictError(error)) throw error;
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'UPDATE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId: id,
          },
        },
        error,
      );
    }
  }

  async deleteThread({ threadId }: { threadId: string }): Promise<void> {
    // Delete thread metadata and sorted set
    const threadKey = getKey(TABLE_THREADS, { id: threadId });
    const threadMessagesKey = getThreadMessagesKey(threadId);
    try {
      const messageIds: string[] = await this.client.zrange(threadMessagesKey, 0, -1);

      const pipeline = this.client.pipeline();
      pipeline.del(threadKey);
      pipeline.del(threadMessagesKey);

      for (let i = 0; i < messageIds.length; i++) {
        const messageId = messageIds[i];
        const messageKey = getMessageKey(threadId, messageId as string);
        pipeline.del(messageKey);
      }

      await pipeline.exec();

      // Bulk delete all message keys for this thread if any remain
      await this.#db.scanAndDelete(getMessageKey(threadId, '*'));
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'DELETE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId,
          },
        },
        error,
      );
    }
  }

  async saveMessages(args: {
    messages: MastraDBMessage[];
    fence?: RunFence;
  }): Promise<{ messages: MastraDBMessage[] }> {
    const { messages } = args;
    const check = this.#runFenceCheck(args.fence, 'saveMessages');
    if (messages.length === 0) return { messages: [] };

    try {
      for (const message of messages) {
        if (!message.threadId) {
          throw new Error('Thread ID is required');
        }
        if (!message.resourceId) {
          throw new Error(
            `Expected to find a resourceId for message, but couldn't find one. An unexpected error has occurred.`,
          );
        }
      }
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'SAVE_MESSAGES', 'INVALID_ARGS'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.USER,
        },
        error,
      );
    }

    // Add an index to each message to maintain order
    const messagesWithIndex = messages.map((message, index) => {
      return {
        ...message,
        _index: index,
      };
    });

    try {
      const batchSize = 1000;
      const targetThreadIds = new Set(messagesWithIndex.map(message => message.threadId!));
      const existingThreadIds: (string | null)[] = [];
      const touchedThreadIds = new Set<string>(targetThreadIds);

      // Read index entries up front so all touched threads can be validated/loaded before writes.
      for (let i = 0; i < messagesWithIndex.length; i += batchSize) {
        const batch = messagesWithIndex.slice(i, i + batchSize);
        const indexLookupPipeline = this.client.pipeline();

        batch.forEach(message => {
          indexLookupPipeline.get(getMessageIndexKey(message.id));
        });
        const batchExistingThreadIds = (await indexLookupPipeline.exec()) as (string | null)[];
        existingThreadIds.push(...batchExistingThreadIds);

        batchExistingThreadIds.forEach(existingThreadId => {
          if (existingThreadId) {
            touchedThreadIds.add(existingThreadId);
          }
        });
      }

      const touchedThreadIdList = Array.from(touchedThreadIds);
      const threadLookupPipeline = this.client.pipeline();
      touchedThreadIdList.forEach(touchedThreadId => {
        threadLookupPipeline.get(getKey(TABLE_THREADS, { id: touchedThreadId }));
      });
      const threadLookupResults = (await threadLookupPipeline.exec()) as (StorageThreadType | null)[];
      const threadRecordsById = new Map<string, StorageThreadType>();

      touchedThreadIdList.forEach((touchedThreadId, index) => {
        const threadRecord = threadLookupResults[index];
        if (threadRecord) {
          threadRecordsById.set(touchedThreadId, threadRecord);
        }
      });

      for (const targetThreadId of targetThreadIds) {
        if (!threadRecordsById.has(targetThreadId)) {
          throw new MastraError(
            {
              id: createStorageErrorId('UPSTASH', 'SAVE_MESSAGES', 'INVALID_ARGS'),
              domain: ErrorDomain.STORAGE,
              category: ErrorCategory.USER,
            },
            new Error(`Thread ${targetThreadId} not found`),
          );
        }
      }

      for (let i = 0; i < messagesWithIndex.length; i += batchSize) {
        const batch = messagesWithIndex.slice(i, i + batchSize);
        const pipeline = writeBatch(this.client, check);
        const batchTouchedThreadIds = new Set<string>();
        const batchExistingThreadIds = existingThreadIds.slice(i, i + batch.length);

        for (const [batchIndex, message] of batch.entries()) {
          const key = getMessageKey(message.threadId!, message.id);
          const createdAtScore = new Date(message.createdAt).getTime();
          const score = message._index !== undefined ? message._index : createdAtScore;
          batchTouchedThreadIds.add(message.threadId!);

          // Check if this message id exists in another thread (index lookup, no scan)
          const existingThreadId = batchExistingThreadIds[batchIndex];
          if (existingThreadId && existingThreadId !== message.threadId) {
            pipeline.del(getMessageKey(existingThreadId, message.id));
            pipeline.zrem(getThreadMessagesKey(existingThreadId), message.id);
            batchTouchedThreadIds.add(existingThreadId);
          }

          // Store the message data
          pipeline.set(key, message);

          // Store the message ID -> threadId index for fast lookups
          pipeline.set(getMessageIndexKey(message.id), message.threadId!);

          // Add to sorted set for this thread
          pipeline.zadd(getThreadMessagesKey(message.threadId!), {
            score,
            member: message.id,
          });
        }

        const now = new Date();
        for (const touchedThreadId of batchTouchedThreadIds) {
          const existingThread = threadRecordsById.get(touchedThreadId);
          if (!existingThread) {
            continue;
          }
          const updatedThread = {
            ...existingThread,
            updatedAt: now,
          };
          const threadKey = getKey(TABLE_THREADS, { id: touchedThreadId });
          pipeline.set(threadKey, processRecord(TABLE_THREADS, updatedThread).processedRecord);
          threadRecordsById.set(touchedThreadId, updatedThread);
        }

        await pipeline.exec();
      }

      const list = new MessageList().add(messages as any, 'memory');
      return { messages: list.get.all.db() };
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'SAVE_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadIds: Array.from(new Set(messages.map(message => message.threadId).filter(Boolean))).join(','),
          },
        },
        error,
      );
    }
  }

  /**
   * Lookup threadId for a message - tries index first (O(1)), falls back to scan (backwards compatible)
   */
  private async _getThreadIdForMessage(messageId: string): Promise<string | null> {
    // Try the index first (fast path for new messages)
    const indexedThreadId = await this.client.get<string>(getMessageIndexKey(messageId));
    if (indexedThreadId) {
      return indexedThreadId;
    }

    // Fall back to scan for backwards compatibility (old messages without index)
    const existingKeyPattern = getMessageKey('*', messageId);
    const keys = await this.#db.scanKeys(existingKeyPattern);
    if (keys.length === 0) return null;

    // Get the message to find its threadId
    const messageData = await this.client.get<MastraDBMessage>(keys[0] as string);
    if (!messageData) return null;

    // Backfill the index for future lookups
    if (messageData.threadId) {
      await this.client.set(getMessageIndexKey(messageId), messageData.threadId);
    }

    return messageData.threadId || null;
  }

  private _sortMessages(messages: MastraDBMessage[], field: string, direction: string): MastraDBMessage[] {
    return messages.sort((a, b) => {
      const getVal = (msg: MastraDBMessage): number => {
        if (field === 'createdAt') {
          return new Date(msg.createdAt).getTime();
        }
        const value = (msg as Record<string, unknown>)[field];
        if (typeof value === 'number') return value;
        if (value instanceof Date) return value.getTime();
        return 0;
      };
      const aValue = getVal(a);
      const bValue = getVal(b);
      return direction === 'ASC' ? aValue - bValue : bValue - aValue;
    });
  }

  /**
   * Fetches the messages named by `include` together with their surrounding context.
   *
   * @param include - Message ids to pin, each with an optional before/after window.
   * @param resourceId - When set, drops any pinned or context message owned by another
   * resource so an id from another resource returns nothing.
   */
  private async _getIncludedMessages(
    include: StorageListMessagesInput['include'],
    resourceId?: string,
  ): Promise<MastraDBMessage[]> {
    if (!include?.length) return [];

    const messageIds = new Set<string>();
    const messageIdToThreadIds: Record<string, string> = {};

    for (const item of include) {
      // Step 1: Find the threadId for this message (index first, then scan)
      const itemThreadId = await this._getThreadIdForMessage(item.id);
      if (!itemThreadId) continue;

      const itemThreadMessagesKey = getThreadMessagesKey(itemThreadId);

      if (resourceId !== undefined) {
        const threadMessageIds = (await this.client.zrange(itemThreadMessagesKey, 0, -1)) as string[];
        const threadPipeline = this.client.pipeline();
        threadMessageIds.forEach(id => threadPipeline.get(getMessageKey(itemThreadId, id)));
        const threadMessages = (await threadPipeline.exec())
          .filter((message): message is MastraDBMessage => message !== null)
          .filter(message => message.resourceId === resourceId);
        const targetIndex = threadMessages.findIndex(message => message.id === item.id);
        if (targetIndex === -1) continue;

        const start = Math.max(0, targetIndex - (item.withPreviousMessages ?? 0));
        const end = Math.min(threadMessages.length, targetIndex + (item.withNextMessages ?? 0) + 1);
        for (const message of threadMessages.slice(start, end)) {
          messageIds.add(message.id);
          messageIdToThreadIds[message.id] = itemThreadId;
        }
        continue;
      }

      messageIds.add(item.id);
      messageIdToThreadIds[item.id] = itemThreadId;

      // Get the rank of this message in the sorted set
      const rank = await this.client.zrank(itemThreadMessagesKey, item.id);
      if (rank === null) continue;

      // Get previous messages if requested
      if (item.withPreviousMessages) {
        const start = Math.max(0, rank - item.withPreviousMessages);
        const prevIds = rank === 0 ? [] : await this.client.zrange(itemThreadMessagesKey, start, rank - 1);
        prevIds.forEach(id => {
          messageIds.add(id as string);
          messageIdToThreadIds[id as string] = itemThreadId;
        });
      }

      // Get next messages if requested
      if (item.withNextMessages) {
        const nextIds = await this.client.zrange(itemThreadMessagesKey, rank + 1, rank + item.withNextMessages);
        nextIds.forEach(id => {
          messageIds.add(id as string);
          messageIdToThreadIds[id as string] = itemThreadId;
        });
      }
    }

    if (messageIds.size === 0) return [];

    const pipeline = this.client.pipeline();
    Array.from(messageIds).forEach(id => {
      const tId = messageIdToThreadIds[id]!;
      pipeline.get(getMessageKey(tId, id as string));
    });
    const results = await pipeline.exec();
    const includedMessages = results.filter(result => result !== null) as MastraDBMessage[];
    return resourceId ? includedMessages.filter(message => message.resourceId === resourceId) : includedMessages;
  }

  private parseStoredMessage(storedMessage: MastraDBMessage & { _index?: number }): MastraDBMessage {
    const defaultMessageContent = { format: 2, parts: [{ type: 'text', text: '' }] };
    const { _index, ...rest } = storedMessage;
    return {
      ...rest,
      createdAt: new Date(rest.createdAt),
      content: rest.content || defaultMessageContent,
    } satisfies MastraDBMessage;
  }

  public async listMessagesById({ messageIds }: { messageIds: string[] }): Promise<{ messages: MastraDBMessage[] }> {
    if (messageIds.length === 0) return { messages: [] };

    try {
      const rawMessages: (MastraDBMessage & { _index?: number })[] = [];

      // Try to get threadIds from index first (fast path)
      const indexPipeline = this.client.pipeline();
      messageIds.forEach(id => indexPipeline.get(getMessageIndexKey(id)));
      const indexResults = await indexPipeline.exec();

      const indexedIds: { messageId: string; threadId: string }[] = [];
      const unindexedIds: string[] = [];

      messageIds.forEach((id, i) => {
        const threadId = indexResults[i] as string | null;
        if (threadId) {
          indexedIds.push({ messageId: id, threadId });
        } else {
          unindexedIds.push(id);
        }
      });

      // Fetch indexed messages directly (O(1) per message)
      if (indexedIds.length > 0) {
        const messagePipeline = this.client.pipeline();
        indexedIds.forEach(({ messageId, threadId }) => messagePipeline.get(getMessageKey(threadId, messageId)));
        const messageResults = await messagePipeline.exec();
        rawMessages.push(...(messageResults.filter(msg => msg !== null) as (MastraDBMessage & { _index?: number })[]));
      }

      // Fall back to scan for unindexed messages (backwards compatibility)
      if (unindexedIds.length > 0) {
        const threadKeys = await this.client.keys('thread:*');

        const result = await Promise.all(
          threadKeys.map(threadKey => {
            const threadId = threadKey.split(':')[1];
            if (!threadId) throw new Error(`Failed to parse thread ID from thread key "${threadKey}"`);
            return this.client.mget<(MastraDBMessage & { _index?: number })[]>(
              unindexedIds.map(id => getMessageKey(threadId, id)),
            );
          }),
        );

        const foundMessages = result.flat(1).filter(msg => !!msg) as (MastraDBMessage & { _index?: number })[];
        rawMessages.push(...foundMessages);

        // Backfill index for found messages
        if (foundMessages.length > 0) {
          const backfillPipeline = this.client.pipeline();
          foundMessages.forEach(msg => {
            if (msg.threadId) {
              backfillPipeline.set(getMessageIndexKey(msg.id), msg.threadId);
            }
          });
          await backfillPipeline.exec();
        }
      }

      const list = new MessageList().add(rawMessages.map(this.parseStoredMessage), 'memory');
      return { messages: list.get.all.db() };
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'LIST_MESSAGES_BY_ID', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            messageIds: JSON.stringify(messageIds),
          },
        },
        error,
      );
    }
  }

  public async listMessages(args: StorageListMessagesInput): Promise<StorageListMessagesOutput> {
    const { threadId, resourceId, include, filter, perPage: perPageInput, page = 0, orderBy } = args;

    // Normalize threadId to array
    const threadIds = Array.isArray(threadId) ? threadId : [threadId];

    if (threadIds.length === 0 || threadIds.some(id => !id.trim())) {
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'LIST_MESSAGES', 'INVALID_THREAD_ID'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { threadId: Array.isArray(threadId) ? threadId.join(',') : threadId },
        },
        new Error('threadId must be a non-empty string or array of non-empty strings'),
      );
    }

    const perPage = normalizePerPage(perPageInput, 40);
    // When perPage is false (get all), ignore page offset
    const { offset, perPage: perPageForResponse } = calculatePagination(page, perPageInput, perPage);
    const metadataFilter = validateStorageMetadataFilter(filter?.metadata);

    try {
      if (page < 0) {
        throw new MastraError(
          {
            id: createStorageErrorId('UPSTASH', 'LIST_MESSAGES', 'INVALID_PAGE'),
            domain: ErrorDomain.STORAGE,
            category: ErrorCategory.USER,
            details: { page },
          },
          new Error('page must be >= 0'),
        );
      }

      // Determine sort field and direction, default to ASC (oldest first)
      const { field, direction } = this.parseOrderBy(orderBy, 'ASC');

      // When perPage is 0 with no includes, there's nothing to return.
      if (perPage === 0 && (!include || include.length === 0)) {
        return { messages: [], total: 0, page, perPage: perPageForResponse, hasMore: false };
      }

      // Get included messages with context if specified
      let includedMessages: MastraDBMessage[] = [];
      if (include && include.length > 0) {
        const included = (await this._getIncludedMessages(include, resourceId)) as MastraDBMessage[];
        includedMessages = included.map(this.parseStoredMessage);
      }

      // When perPage is 0, we only need included messages — skip thread load entirely
      if (perPage === 0 && include && include.length > 0) {
        const list = new MessageList().add(includedMessages, 'memory');
        return {
          messages: this._sortMessages(list.get.all.db(), field, direction),
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      // Get all message IDs from all thread sorted sets
      const allMessageIdsWithThreads: { threadId: string; messageId: string }[] = [];
      for (const tid of threadIds) {
        const threadMessagesKey = getThreadMessagesKey(tid);
        const messageIds = await this.client.zrange(threadMessagesKey, 0, -1);
        for (const mid of messageIds) {
          allMessageIdsWithThreads.push({ threadId: tid, messageId: mid as string });
        }
      }

      if (allMessageIdsWithThreads.length === 0) {
        return {
          messages: [],
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      // Use pipeline to fetch all messages efficiently
      const pipeline = this.client.pipeline();
      allMessageIdsWithThreads.forEach(({ threadId: tid, messageId }) => pipeline.get(getMessageKey(tid, messageId)));
      const results = await pipeline.exec();

      // Process messages and apply filters
      let messagesData = results
        .filter((msg): msg is MastraDBMessage & { _index?: number } => msg !== null)
        .map(this.parseStoredMessage);

      // Filter by resourceId if provided
      if (resourceId) {
        messagesData = messagesData.filter(msg => msg.resourceId === resourceId);
      }

      // Apply date filters if provided
      messagesData = filterByDateRange(
        messagesData,
        (msg: MastraDBMessage) => new Date(msg.createdAt),
        filter?.dateRange,
      );

      messagesData = messagesData.filter(message =>
        storageMessageMatchesMetadataFilter(message.content, metadataFilter),
      );

      // Always sort messages by the sort field/direction before pagination
      // This ensures consistent ordering whether orderBy is explicit or uses the default (createdAt ASC)
      messagesData = this._sortMessages(messagesData, field, direction);

      const total = messagesData.length;

      // Apply pagination
      const start = offset;
      const end = perPageInput === false ? total : start + perPage;
      const paginatedMessages = messagesData.slice(start, end);

      // Combine paginated messages with included messages, deduplicating
      const messageIds = new Set<string>();
      const allMessages: MastraDBMessage[] = [];

      // Add paginated messages first
      for (const msg of paginatedMessages) {
        if (!messageIds.has(msg.id)) {
          allMessages.push(msg);
          messageIds.add(msg.id);
        }
      }

      // Add included messages (with context), avoiding duplicates
      for (const msg of includedMessages) {
        if (!messageIds.has(msg.id)) {
          allMessages.push(msg);
          messageIds.add(msg.id);
        }
      }

      // Use MessageList for proper deduplication and format conversion
      const list = new MessageList().add(allMessages, 'memory');
      let finalMessages = list.get.all.db();

      // Sort all messages (paginated + included) for final output - must be done AFTER MessageList
      // because MessageList.get.all.db() sorts by createdAt ASC internally
      // Always sort by createdAt (or specified field) to ensure consistent chronological ordering
      // This is critical when `include` parameter brings in messages from semantic recall
      finalMessages = this._sortMessages(finalMessages, field, direction);

      const threadIdSet = new Set(threadIds);
      const returnedThreadMessageIds = new Set(
        finalMessages
          .filter(message => message.threadId && threadIdSet.has(message.threadId))
          .map(message => message.id),
      );
      const allThreadMessagesReturned = returnedThreadMessageIds.size >= total;
      const hasMore = metadataFilter
        ? perPageInput !== false && offset + paginatedMessages.length < total
        : perPageInput !== false && !allThreadMessagesReturned && offset + perPage < total;

      return {
        messages: finalMessages,
        total,
        page,
        perPage: perPageForResponse,
        hasMore,
      };
    } catch (error) {
      // Re-throw USER errors (validation errors) directly so callers get proper 400 responses
      if (error instanceof MastraError && error.category === ErrorCategory.USER) {
        throw error;
      }
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'LIST_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId: Array.isArray(threadId) ? threadId.join(',') : threadId,
            resourceId: resourceId ?? '',
          },
        },
        error,
      );
      this.logger.error(mastraError.toString());
      this.logger?.trackException(mastraError);
      throw mastraError;
    }
  }

  async getResourceById({ resourceId }: { resourceId: string }): Promise<StorageResourceType | null> {
    try {
      const key = `${TABLE_RESOURCES}:${resourceId}`;
      const data = await this.client.get<StorageResourceType>(key);

      if (!data) {
        return null;
      }

      return {
        ...data,
        createdAt: new Date(data.createdAt),
        updatedAt: new Date(data.updatedAt),
        // Ensure workingMemory is always returned as a string, regardless of automatic parsing
        workingMemory: typeof data.workingMemory === 'object' ? JSON.stringify(data.workingMemory) : data.workingMemory,
        metadata: typeof data.metadata === 'string' ? JSON.parse(data.metadata) : data.metadata,
      };
    } catch (error) {
      this.logger.error('Error getting resource by ID:', error);
      throw error;
    }
  }

  async saveResource({ resource }: { resource: StorageResourceType }): Promise<StorageResourceType> {
    return this.#saveResource(resource, undefined);
  }

  async #saveResource(resource: StorageResourceType, check: RunFenceCheck | undefined): Promise<StorageResourceType> {
    try {
      const key = `${TABLE_RESOURCES}:${resource.id}`;
      const serializedResource = {
        ...resource,
        metadata: JSON.stringify(resource.metadata),
        createdAt: resource.createdAt.toISOString(),
        updatedAt: resource.updatedAt.toISOString(),
      };

      const batch = writeBatch(this.client, check);
      batch.set(key, serializedResource);
      await batch.exec();

      return resource;
    } catch (error) {
      if (!isRunFenceConflictError(error)) this.logger.error('Error saving resource:', error);
      throw error;
    }
  }

  async updateResource({
    resourceId,
    workingMemory,
    metadata,
    fence,
  }: {
    resourceId: string;
    workingMemory?: string;
    metadata?: Record<string, unknown>;
    fence?: RunFence;
  }): Promise<StorageResourceType> {
    const check = this.#runFenceCheck(fence, 'updateResource');
    try {
      const existingResource = await this.getResourceById({ resourceId });

      if (!existingResource) {
        // Create new resource if it doesn't exist
        const newResource: StorageResourceType = {
          id: resourceId,
          workingMemory,
          metadata: metadata || {},
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        return await this.#saveResource(newResource, check);
      }

      const updatedResource = {
        ...existingResource,
        workingMemory: workingMemory !== undefined ? workingMemory : existingResource.workingMemory,
        metadata: {
          ...existingResource.metadata,
          ...metadata,
        },
        updatedAt: new Date(),
      };

      await this.#saveResource(updatedResource, check);
      return updatedResource;
    } catch (error) {
      if (!isRunFenceConflictError(error)) this.logger.error('Error updating resource:', error);
      throw error;
    }
  }

  async updateMessages(args: {
    messages: (Partial<Omit<MastraDBMessage, 'createdAt'>> & {
      id: string;
      content?: { metadata?: MastraMessageContentV2['metadata']; content?: MastraMessageContentV2['content'] };
    })[];
    fence?: RunFence;
  }): Promise<MastraDBMessage[]> {
    const { messages } = args;

    if (messages.length === 0) {
      return [];
    }
    const check = this.#runFenceCheck(args.fence, 'updateMessages');

    try {
      // Get all message IDs to update
      const messageIds = messages.map(m => m.id);
      const updatesById = new Map(messages.map(message => [message.id, message]));

      // Find all existing messages — try index first, fall back to scan
      const existingMessages: MastraDBMessage[] = [];
      const messageIdToKey: Record<string, string> = {};
      const backfillIndexValues: Record<string, string> = {};

      const indexPipeline = this.client.pipeline();
      messageIds.forEach(messageId => indexPipeline.get(getMessageIndexKey(messageId)));
      const indexResults = (await indexPipeline.exec()) as (string | null)[];

      const indexedLookups: { messageId: string; threadId: string }[] = [];
      const fallbackMessageIds: string[] = [];

      messageIds.forEach((messageId, index) => {
        const indexedThreadId = indexResults[index];
        if (indexedThreadId) {
          indexedLookups.push({ messageId, threadId: indexedThreadId });
        } else {
          fallbackMessageIds.push(messageId);
        }
      });

      if (indexedLookups.length > 0) {
        const messagePipeline = this.client.pipeline();
        indexedLookups.forEach(({ messageId, threadId }) => {
          messagePipeline.get(getMessageKey(threadId, messageId));
        });
        const indexedMessages = (await messagePipeline.exec()) as (MastraDBMessage | null)[];

        indexedLookups.forEach(({ messageId, threadId }, index) => {
          const key = getMessageKey(threadId, messageId);
          const message = indexedMessages[index];
          if (message && message.id === messageId) {
            existingMessages.push(message);
            messageIdToKey[messageId] = key;
          } else {
            fallbackMessageIds.push(messageId);
          }
        });
      }

      for (const messageId of fallbackMessageIds) {
        // Fall back to scan for backwards compatibility (old messages without index)
        const pattern = getMessageKey('*', messageId);
        const keys = await this.#db.scanKeys(pattern);

        for (const key of keys) {
          const message = await this.client.get<MastraDBMessage>(key);
          if (message && message.id === messageId) {
            existingMessages.push(message);
            messageIdToKey[messageId] = key;
            // Backfill the index for future lookups
            if (message.threadId) {
              backfillIndexValues[messageId] = message.threadId;
            }
            break;
          }
        }
      }

      if (Object.keys(backfillIndexValues).length > 0) {
        const backfillPipeline = this.client.pipeline();
        for (const [messageId, threadId] of Object.entries(backfillIndexValues)) {
          backfillPipeline.set(getMessageIndexKey(messageId), threadId);
        }
        await backfillPipeline.exec();
      }

      if (existingMessages.length === 0) {
        await assertRunFence(this.client, check);
        return [];
      }

      const threadIdsToUpdate = new Set<string>();
      const destinationThreadIds = new Set<string>();

      for (const existingMessage of existingMessages) {
        const updatePayload = updatesById.get(existingMessage.id);
        if (!updatePayload) continue;

        const { id: _id, ...fieldsToUpdate } = updatePayload;
        if (Object.keys(fieldsToUpdate).length === 0) continue;

        threadIdsToUpdate.add(existingMessage.threadId!);
        if (updatePayload.threadId && updatePayload.threadId !== existingMessage.threadId) {
          threadIdsToUpdate.add(updatePayload.threadId);
          destinationThreadIds.add(updatePayload.threadId);
        }
      }

      const threadRecordsById = new Map<string, StorageThreadType>();
      if (threadIdsToUpdate.size > 0) {
        const threadIdList = Array.from(threadIdsToUpdate);
        const threadLookupPipeline = this.client.pipeline();

        threadIdList.forEach(threadId => {
          threadLookupPipeline.get(getKey(TABLE_THREADS, { id: threadId }));
        });
        const threadLookupResults = (await threadLookupPipeline.exec()) as (StorageThreadType | null)[];

        threadIdList.forEach((threadId, index) => {
          const threadRecord = threadLookupResults[index];
          if (threadRecord) {
            threadRecordsById.set(threadId, threadRecord);
          }
        });
      }

      for (const destinationThreadId of destinationThreadIds) {
        if (!threadRecordsById.has(destinationThreadId)) {
          throw new MastraError(
            {
              id: createStorageErrorId('UPSTASH', 'UPDATE_MESSAGES', 'INVALID_ARGS'),
              domain: ErrorDomain.STORAGE,
              category: ErrorCategory.USER,
            },
            new Error(`Thread ${destinationThreadId} not found`),
          );
        }
      }

      const pipeline = writeBatch(this.client, check);

      // Process each existing message for updates
      for (const existingMessage of existingMessages) {
        const updatePayload = updatesById.get(existingMessage.id);
        if (!updatePayload) continue;

        const { id, ...fieldsToUpdate } = updatePayload;
        if (Object.keys(fieldsToUpdate).length === 0) continue;

        // Create updated message object
        const updatedMessage = { ...existingMessage };

        // Special handling for the content field to merge instead of overwrite
        if (fieldsToUpdate.content) {
          const existingContent = existingMessage.content as MastraMessageContentV2;
          const newContent = {
            ...existingContent,
            ...fieldsToUpdate.content,
            // Deep merge metadata if it exists on both
            ...(existingContent?.metadata && fieldsToUpdate.content.metadata
              ? {
                  metadata: {
                    ...existingContent.metadata,
                    ...fieldsToUpdate.content.metadata,
                  },
                }
              : {}),
          };
          updatedMessage.content = newContent;
        }

        // Update other fields
        for (const key in fieldsToUpdate) {
          if (Object.prototype.hasOwnProperty.call(fieldsToUpdate, key) && key !== 'content') {
            (updatedMessage as any)[key] = fieldsToUpdate[key as keyof typeof fieldsToUpdate];
          }
        }

        // Update the message in Redis
        const key = messageIdToKey[id];
        if (key) {
          // If the message is being moved to a different thread, we need to handle the key change
          if (updatePayload.threadId && updatePayload.threadId !== existingMessage.threadId) {
            const newThreadId = updatedMessage.threadId!;

            // Remove from old thread's sorted set
            const oldThreadMessagesKey = getThreadMessagesKey(existingMessage.threadId!);
            pipeline.zrem(oldThreadMessagesKey, id);

            // Delete the old message key
            pipeline.del(key);

            // Create new message key with new threadId
            const newKey = getMessageKey(newThreadId, id);
            pipeline.set(newKey, updatedMessage);
            pipeline.set(getMessageIndexKey(id), newThreadId);
            messageIdToKey[id] = newKey;

            // Add to new thread's sorted set
            const newThreadMessagesKey = getThreadMessagesKey(newThreadId);
            const score =
              (updatedMessage as any)._index !== undefined
                ? (updatedMessage as any)._index
                : new Date(updatedMessage.createdAt).getTime();
            pipeline.zadd(newThreadMessagesKey, { score, member: id });
          } else {
            // No thread change, just update the existing key
            pipeline.set(key, updatedMessage);
          }
        }
      }

      // Update thread timestamps
      const now = new Date();
      for (const threadId of threadIdsToUpdate) {
        if (threadId) {
          const existingThread = threadRecordsById.get(threadId);
          if (existingThread) {
            const updatedThread = {
              ...existingThread,
              updatedAt: now,
            };
            const threadKey = getKey(TABLE_THREADS, { id: threadId });
            pipeline.set(threadKey, processRecord(TABLE_THREADS, updatedThread).processedRecord);
            threadRecordsById.set(threadId, updatedThread);
          }
        }
      }

      // Execute all updates
      await pipeline.exec();

      // Return the updated messages
      const updatedMessages: MastraDBMessage[] = [];
      for (const messageId of messageIds) {
        const key = messageIdToKey[messageId];
        if (key) {
          const updatedMessage = await this.client.get<MastraDBMessage>(key);
          if (updatedMessage) {
            updatedMessages.push(updatedMessage);
          }
        }
      }

      return updatedMessages;
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'UPDATE_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            messageIds: messages.map(m => m.id).join(','),
          },
        },
        error,
      );
    }
  }

  async deleteMessages(messageIds: string[], options?: { fence?: RunFence }): Promise<void> {
    if (!messageIds || messageIds.length === 0) {
      return;
    }
    const check = this.#runFenceCheck(options?.fence, 'deleteMessages');

    try {
      const threadIds = new Set<string>();
      const messageKeys: string[] = [];
      const foundMessageIds: string[] = [];

      // Try index first for each message (fast path)
      const indexPipeline = this.client.pipeline();
      messageIds.forEach(id => indexPipeline.get(getMessageIndexKey(id)));
      const indexResults = await indexPipeline.exec();

      const indexedMessages: { messageId: string; threadId: string }[] = [];
      const unindexedMessageIds: string[] = [];

      messageIds.forEach((id, i) => {
        const threadId = indexResults[i] as string | null;
        if (threadId) {
          indexedMessages.push({ messageId: id, threadId });
        } else {
          unindexedMessageIds.push(id);
        }
      });

      // Process indexed messages (fast path)
      for (const { messageId, threadId } of indexedMessages) {
        messageKeys.push(getMessageKey(threadId, messageId));
        foundMessageIds.push(messageId);
        threadIds.add(threadId);
      }

      // Fall back to scan for unindexed messages (backwards compatibility)
      for (const messageId of unindexedMessageIds) {
        const pattern = getMessageKey('*', messageId);
        const keys = await this.#db.scanKeys(pattern);

        for (const key of keys) {
          const message = await this.client.get<MastraDBMessage>(key);
          if (message && message.id === messageId) {
            messageKeys.push(key);
            foundMessageIds.push(messageId);
            if (message.threadId) {
              threadIds.add(message.threadId);
            }
            break;
          }
        }
      }

      if (messageKeys.length === 0) {
        // none of the message ids existed
        await assertRunFence(this.client, check);
        return;
      }

      const pipeline = writeBatch(this.client, check);

      // Delete all messages
      for (const key of messageKeys) {
        pipeline.del(key);
      }

      // Delete all message index entries
      for (const messageId of foundMessageIds) {
        pipeline.del(getMessageIndexKey(messageId));
      }

      // Update thread timestamps
      if (threadIds.size > 0) {
        for (const threadId of threadIds) {
          const threadKey = getKey(TABLE_THREADS, { id: threadId });
          const thread = await this.client.get<StorageThreadType>(threadKey);
          if (thread) {
            const updatedThread = {
              ...thread,
              updatedAt: new Date(),
            };
            pipeline.set(threadKey, processRecord(TABLE_THREADS, updatedThread).processedRecord);
          }
        }
      }

      // Execute all operations
      await pipeline.exec();

      // TODO: Delete from vector store if semantic recall is enabled
    } catch (error) {
      if (isRunFenceConflictError(error)) throw error;
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'DELETE_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { messageIds: messageIds.join(', ') },
        },
        error,
      );
    }
  }

  private sortThreads(
    threads: StorageThreadType[],
    field: ThreadOrderBy,
    direction: ThreadSortDirection,
  ): StorageThreadType[] {
    return threads.sort((a, b) => {
      const aValue = new Date(a[field]).getTime();
      const bValue = new Date(b[field]).getTime();

      if (direction === 'ASC') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  }

  async cloneThread(args: StorageCloneThreadInput): Promise<StorageCloneThreadOutput> {
    const { sourceThreadId, newThreadId: providedThreadId, resourceId, title, metadata, options } = args;

    // Get the source thread
    const sourceThread = await this.getThreadById({ threadId: sourceThreadId });
    if (!sourceThread) {
      throw new MastraError({
        id: createStorageErrorId('UPSTASH', 'CLONE_THREAD', 'SOURCE_NOT_FOUND'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.USER,
        text: `Source thread with id ${sourceThreadId} not found`,
        details: { sourceThreadId },
      });
    }

    // Use provided ID or generate a new one
    const newThreadId = providedThreadId || crypto.randomUUID();

    // Check if the new thread ID already exists
    const existingThread = await this.getThreadById({ threadId: newThreadId });
    if (existingThread) {
      throw new MastraError({
        id: createStorageErrorId('UPSTASH', 'CLONE_THREAD', 'THREAD_EXISTS'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.USER,
        text: `Thread with id ${newThreadId} already exists`,
        details: { newThreadId },
      });
    }

    try {
      // Get all message IDs from the source thread's sorted set
      const threadMessagesKey = getThreadMessagesKey(sourceThreadId);
      const messageIds = await this.client.zrange(threadMessagesKey, 0, -1);

      // Fetch all source messages
      const pipeline = this.client.pipeline();
      for (const mid of messageIds) {
        pipeline.get(getMessageKey(sourceThreadId, mid as string));
      }
      const results = await pipeline.exec();

      // Parse and filter messages
      let sourceMessages = results
        .filter((msg): msg is MastraDBMessage & { _index?: number } => msg !== null)
        .map(msg => ({
          ...msg,
          createdAt: new Date(msg.createdAt),
        }));

      // Apply date filters
      if (options?.messageFilter?.startDate || options?.messageFilter?.endDate) {
        sourceMessages = filterByDateRange(sourceMessages, (msg: MastraDBMessage) => new Date(msg.createdAt), {
          start: options.messageFilter?.startDate,
          end: options.messageFilter?.endDate,
        });
      }

      // Apply message ID filter
      if (options?.messageFilter?.messageIds && options.messageFilter.messageIds.length > 0) {
        const messageIdSet = new Set(options.messageFilter.messageIds);
        sourceMessages = sourceMessages.filter(msg => messageIdSet.has(msg.id));
      }

      // Sort by createdAt ASC
      sourceMessages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      // Apply message limit (from most recent)
      if (options?.messageLimit && options.messageLimit > 0 && sourceMessages.length > options.messageLimit) {
        sourceMessages = sourceMessages.slice(-options.messageLimit);
      }

      const now = new Date();

      // Determine the last message ID for clone metadata
      const lastMessageId = sourceMessages.length > 0 ? sourceMessages[sourceMessages.length - 1]!.id : undefined;

      // Create clone metadata
      const cloneMetadata: ThreadCloneMetadata = {
        sourceThreadId,
        clonedAt: now,
        ...(lastMessageId && { lastMessageId }),
      };

      // Create the new thread
      const newThread: StorageThreadType = {
        id: newThreadId,
        resourceId: resourceId || sourceThread.resourceId,
        title: title || (sourceThread.title ? `Clone of ${sourceThread.title}` : ''),
        metadata: {
          ...metadata,
          clone: cloneMetadata,
        },
        createdAt: now,
        updatedAt: now,
      };

      // Use pipeline for all writes
      const writePipeline = this.client.pipeline();

      // Save the new thread
      const threadKey = getKey(TABLE_THREADS, { id: newThreadId });
      writePipeline.set(threadKey, processRecord(TABLE_THREADS, newThread).processedRecord);

      // Clone messages with new IDs
      const clonedMessages: MastraDBMessage[] = [];
      const messageIdMap: Record<string, string> = {};
      const targetResourceId = resourceId || sourceThread.resourceId;
      const newThreadMessagesKey = getThreadMessagesKey(newThreadId);

      for (let i = 0; i < sourceMessages.length; i++) {
        const sourceMsg = sourceMessages[i]!;
        const newMessageId = crypto.randomUUID();
        messageIdMap[sourceMsg.id] = newMessageId;
        const { _index, ...restMsg } = sourceMsg as MastraDBMessage & { _index?: number };

        const newMessage: MastraDBMessage = {
          ...restMsg,
          id: newMessageId,
          threadId: newThreadId,
          resourceId: targetResourceId,
        };

        // Store the message data
        const messageKey = getMessageKey(newThreadId, newMessageId);
        writePipeline.set(messageKey, newMessage);

        // Store the message ID -> threadId index for fast lookups
        writePipeline.set(getMessageIndexKey(newMessageId), newThreadId);

        // Add to sorted set for this thread (use index for ordering)
        writePipeline.zadd(newThreadMessagesKey, {
          score: i,
          member: newMessageId,
        });

        clonedMessages.push(newMessage);
      }

      // Execute all writes
      await writePipeline.exec();

      return {
        thread: newThread,
        clonedMessages,
        messageIdMap,
      };
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('UPSTASH', 'CLONE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { sourceThreadId, newThreadId },
        },
        error,
      );
    }
  }
}
